// ─── toggles-logic.ts — pure toggle state + persistence helpers ──────────────
//
// Extracted from toggles.ts (ts-unit-testability-seams-260623).
// ALL exports here are pure (no document / window / localStorage direct access).
// toggles.ts imports from here; DOM wiring stays in toggles.ts.
//
// Consumers:
//   - toggles.ts (DOM glue shim)
//   - web/static/spa/test/toggles-logic.seam.test.ts (unit seam test)
//
// See:
//   docs/design/ts-unit-testability-seams-260623.md §7 W-E
//   docs/design/structural-mods-toggle-redesign-260507.md §11
// ─── Structural-mods constants ────────────────────────────────────────────────
// Hazard-rule ruling (260902, docs/epics/active/frontend-knobs-and-visuals-redesign-260901):
// expose a control ONLY for a genuine author-CHOICE (content DocAble would
// author — speaker notes) OR a real HAZARD the user must accept (sheet renaming
// can break cross-workbook formula references). Everything safe / strictly-
// improving is AUTOMATIC frontend-only: its control is removed AND its leaf
// dropped from SM_LEAVES so the SPA NEVER sends {leaf:false}, while
// assets/structural-mods-defaults.json keeps production_default:true — so the
// pass still runs server-side by default. The now-automatic leaves
// (sm_pdf_outlines_from_ocr, sm_pdf_page_labels, {pdf,pptx,docx,xlsx}_font_repair,
// sm_pptx_reading_order, sm_pptx_sections, sm_pptx_titles, sm_docx_headings)
// were verified DERIVE-not-INVENT: sections/titles disambiguate existing
// duplicate names; headings only renumber/re-level EXISTING heading paragraphs.
// SM_LEAVES and the JSON leaf set diverge by design (automatic leaves stay in JSON).
export const SM_LEAVES = [
    'sm_replace_alt',
    'sm_downsample_images',
    'sm_math_omml_promotion',
    // Cross-format URL-text control (All-formats group): the single
    // "Replace raw URLs with descriptive link text" toggle drives BOTH
    // sm_pptx_url_text and sm_docx_url_text via a shared DOM id in
    // SM_LEAF_TO_ID (see the injective-exception note there).
    'sm_pptx_url_text',
    // Opt-in (default-OFF) — AI-authors speaker notes for slides that have
    // none. Excluded from any sub-master rollup so nothing silently enables
    // content authoring. See docs/epics/active/pptx-speaker-notes-authoring-260719/.
    'sm_pptx_speaker_notes',
    'sm_docx_url_text',
    // HAZARD control (default-OFF): renaming sheets can break formulas that
    // reference sheets by name — especially cross-workbook links. Kept as a
    // control the user opts into, not automatic. See the Excel card risk hint.
    'sm_xlsx_sheet_names',
];
// Spec-default: the frontend default for the leaves the SPA still SENDS
// (SM_LEAVES). This is a SUBSET of assets/structural-mods-defaults.json: the
// automatic leaves (hazard-rule ruling) are absent here (see the SM_LEAVES
// comment) but remain in the JSON at production_default:true — so their pass
// runs server-side by default. Every leaf below defaults `true` EXCEPT the
// three default-OFF leaves:
//   - sm_pptx_speaker_notes — AUTHORS new content, so it ships unchecked and
//     its unchecked state is the consent surface.
//   - sm_downsample_images  — lossy: it can plausibly degrade image fidelity,
//     so it ships OFF as an opt-in escape hatch (user ruling 2026-09-01,
//     reversing the 260508 default-on; spec organizing rule).
//   - sm_xlsx_sheet_names   — HAZARD: renaming sheets can break formula/name
//     references (esp. cross-workbook), so it ships OFF as an opt-in.
// See docs/epics/active/frontend-knobs-and-visuals-redesign-260901.
// The three match production_default:false in structural-mods-defaults.json.
// WHY: Readonly<Record<string, boolean>> annotation is intentional — keyed by
// leaf: string at runtime (from SM_LEAVES). T|undefined is correct behavior.
export const SM_SPEC_DEFAULT = Object.freeze({
    sm_replace_alt: true,
    sm_downsample_images: false, // opt-in escape hatch — lossy; user ruling 260901 (reversed 260508 default-on)
    sm_math_omml_promotion: true,
    sm_pptx_url_text: true,
    sm_pptx_speaker_notes: false, // opt-in — authors content; default-OFF
    sm_docx_url_text: true,
    sm_xlsx_sheet_names: false, // HAZARD opt-in — renaming sheets can break formula refs (260902 hazard rule)
});
// SM_TREE (master/sub-master chaining) was REMOVED in the 260902 compact
// redesign. The panel is now a flat compact grid: every surviving control is
// an independent knob with no master toggle and no per-format sub-master
// rollup. The hierarchy had become near-vestigial (one single-leaf Excel
// sub-master) and no longer earned its complexity, so the chaining machinery
// (SM_TREE + the rollup functions in toggles.ts) was dropped. Format identity
// is now conveyed statically by the P/X icon next to each format-specific
// control in the markup, not by a runtime tree. See
// docs/epics/active/frontend-knobs-and-visuals-redesign-260901.
// Map between leaf wire-name and DOM element id.
// INJECTIVE EXCEPTION (260902 cut): sm_pptx_url_text + sm_docx_url_text
// intentionally share ONE DOM control — the All-formats "Replace raw URLs
// with descriptive link text" toggle (#sm-url-text). One control drives both
// wire leaves so a user makes a single cross-format URL choice; both leaves
// read/persist from that shared checkbox. This is the sole non-injective pair
// (see toggles-logic.doc-derived.test.ts Claim 4).
// WHY: Readonly<Record<string, string>> annotation is intentional — keyed by
// leaf: string at runtime. T|undefined is handled at callsites.
export const SM_LEAF_TO_ID = Object.freeze({
    sm_replace_alt: 'sm-replace-alt',
    sm_downsample_images: 'sm-downsample-images',
    sm_math_omml_promotion: 'sm-math-omml-promotion',
    sm_pptx_url_text: 'sm-url-text',
    sm_pptx_speaker_notes: 'sm-pptx-speaker-notes',
    sm_docx_url_text: 'sm-url-text',
    sm_xlsx_sheet_names: 'sm-xlsx-sheet-names',
});
// ─── Persistence key constants ────────────────────────────────────────────────
export const SM_LS_KEY_PREFIX = 'ada_tool:sm:';
export const USER_CONTEXT_ALT_TEXT_MAX_CHARS = 2000;
export const USER_CONTEXT_LS_TEXT = 'adatool.user_context_alt_text';
export const USER_CONTEXT_LS_ENABLED = 'adatool.user_context_alt_text_enabled';
export const AUDIENCE_LEVEL_LS_INDEX = 'adatool.audience_level_index';
// ─── Audience-level constants ─────────────────────────────────────────────────
export const AUDIENCE_LEVEL_BY_INDEX = [
    null, // 0 = Off
    'K3', // 1 = K-3
    '4to6', // 2 = 4-6
    '7to9', // 3 = 7-9
    '10to12', // 4 = 10-12
    'LowerCollege', // 5 = Lower-level college
    'UpperCollege', // 6 = Upper-level college
    'Graduate', // 7 = Graduate
    'Professional', // 8 = Professional
];
export const AUDIENCE_LEVEL_LABELS = [
    'Off', 'K-3', '4-6', '7-9', '10-12',
    'College: Lower-level', 'College: Upper-level', 'Graduate', 'Professional',
];
// ─── Pure state-computation helpers ──────────────────────────────────────────
/**
 * Compute initial SM leaf states for an authenticated user.
 * user.structural_mod_prefs is the stored default (server-authoritative).
 * Missing keys fall back to SM_SPEC_DEFAULT.
 */
export function smInitialLeavesForUser(user) {
    const stored = user?.structural_mod_prefs ?? {};
    const out = {};
    for (const leaf of SM_LEAVES) {
        out[leaf] = leaf in stored ? !!stored[leaf] : SM_SPEC_DEFAULT[leaf]; // WHY: leaf ∈ SM_LEAVES ⊆ keys(SM_SPEC_DEFAULT)
    }
    return out;
}
/**
 * Compute initial SM leaf states for an anonymous user.
 * Spec-default overlaid with any localStorage overrides.
 * @param getItem — injectable localStorage-reader; pure in tests, real localStorage in production.
 */
export function smInitialLeavesForAnon(getItem) {
    const out = { ...SM_SPEC_DEFAULT };
    for (const leaf of SM_LEAVES) {
        try {
            const stored = getItem(`${SM_LS_KEY_PREFIX}${leaf}`);
            if (stored === '1')
                out[leaf] = true;
            else if (stored === '0')
                out[leaf] = false;
        }
        catch (e) { /* best-effort; storage may be unavailable */
            void e;
        }
    }
    return out;
}
/**
 * Compute upload diff: entries where current[leaf] differs from stored[leaf].
 * Both maps must cover all SM_LEAVES. Returns {} when no overrides.
 * Per §11.5: server uses empty diff to mean "use stored default unchanged."
 */
export function smComputeDiff(current, stored) {
    const diff = {};
    for (const leaf of SM_LEAVES) {
        // WHY: both Records should have entries for all leaves (callers guarantee this)
        if (current[leaf] !== stored[leaf])
            diff[leaf] = current[leaf];
    }
    return diff;
}
/**
 * Map a numeric audience-level index to its wire-format enum string.
 * Returns null for index 0 (Off) or any out-of-range / non-finite index.
 */
export function smAudienceLevelFromIndex(idx) {
    if (!Number.isFinite(idx) || idx <= 0 || idx >= AUDIENCE_LEVEL_BY_INDEX.length)
        return null;
    return AUDIENCE_LEVEL_BY_INDEX[idx]; // WHY: idx bounds-checked above (0 < idx < length)
}
/**
 * Clamp and trim alt-text context to the server-side cap.
 * Returns null when empty / whitespace-only.
 */
export function smClampAltText(text) {
    const v = (text || '').trim();
    if (!v)
        return null;
    return v.length > USER_CONTEXT_ALT_TEXT_MAX_CHARS
        ? v.slice(0, USER_CONTEXT_ALT_TEXT_MAX_CHARS) : v;
}
